import {
  ACCOUNT_REQUIRED_MESSAGE,
  ARENA_STAKES,
  arenaPayout,
  arenaPicks,
  type ArenaBoard,
  type ArenaMomentKind,
  type ArenaPick,
  type ArenaPrivate,
  type ArenaSide,
  type ArenaSport,
  type ArenaState,
} from "@townsy/contracts";

/**
 * The Sports Park stadiums on the client (ADR-0044): pure helpers that turn the server's game state into what the
 * overlay, the big scoreboard and the 3D show. Nothing here decides an outcome; the browser only displays the state and
 * sends intents (cheer, predict, sit, stand).
 */

/** A shared state that looks like a stadium's (anything else is ignored). */
export function asArenaState(shared: unknown): ArenaState | null {
  if (!shared || typeof shared !== "object") return null;
  const s = shared as Partial<ArenaState>;
  if (typeof s.gameNo !== "number" || typeof s.phase !== "string" || !s.board || !s.home || !s.away || !Array.isArray(s.pos)) return null;
  return s as ArenaState;
}

export function asArenaPrivate(mine: unknown): ArenaPrivate | null {
  if (!mine || typeof mine !== "object") return null;
  const m = mine as Partial<ArenaPrivate>;
  if (typeof m.gameNo !== "number" || typeof m.cheerIn !== "number") return null;
  return m as ArenaPrivate;
}

/** "12:00", "7:05", "0:42" (minutes may exceed 59: soccer shows "63:12"). */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Whole seconds until kickoff, counting down locally from when the state arrived. */
export function secondsToKickoff(state: Pick<ArenaState, "kickoffIn">, receivedAtMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((state.kickoffIn - Math.max(0, nowMs - receivedAtMs)) / 1000));
}

export function ordinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

/** The period part of the scoreboard: "Q2", "2nd half", "Top 7th", "P3", "OT", "SO", "Half time", "Final". */
export function periodText(sport: ArenaSport, board: ArenaBoard, phase: ArenaState["phase"]): string {
  if (phase === "pregame") return "Pre-game";
  if (phase === "final" || board.periodLabel === "FINAL") return "Final";
  const l = board.periodLabel;
  if (l === "HT") return "Half time";
  if (l === "INT") return "Intermission";
  if (l === "SO") return "Shootout";
  if (sport === "baseball") return l.replace(/^Bot /, "Bottom ");
  return l;
}

/** The clock part: "7:41" (football, basketball, hockey count down; soccer counts up), baseball's outs and count. */
export function clockText(sport: ArenaSport, board: ArenaBoard, phase: ArenaState["phase"]): string {
  if (sport === "baseball") {
    const b = board.baseball;
    if (!b || phase !== "live") return "";
    return `${b.outs} out${b.outs === 1 ? "" : "s"} · ${b.balls}-${b.strikes}`;
  }
  if (board.periodLabel === "SO" && board.shootout) return `${board.shootout.home}-${board.shootout.away} (round ${board.shootout.round})`;
  if (board.clock === null) return "";
  return formatClock(board.clock);
}

/** Football's down and distance ("2nd & 7 · OPP 35"), "" for the other sports. */
export function downText(board: ArenaBoard): string {
  const f = board.football;
  if (!f) return "";
  return `${ordinal(f.down)} & ${f.goalToGo ? "Goal" : f.toGo} · ${f.ballOn}`;
}

/** Baseball's bases as three dots (first, second, third): "on" or "off". */
export function basesText(board: ArenaBoard): string {
  const b = board.baseball;
  if (!b) return "";
  const names = ["first", "second", "third"];
  const on = b.bases.map((x, i) => (x ? names[i] : null)).filter(Boolean);
  return on.length === 0 ? "Bases empty" : on.length === 3 ? "Bases loaded" : `On ${on.join(" and ")}`;
}

/** The overlay's status line. */
export function headline(state: ArenaState, kickoffSeconds: number): { title: string; detail: string } {
  const { home, away } = state;
  switch (state.phase) {
    case "pregame":
      return { title: `Next game: ${home.name} vs ${away.name}`, detail: `in ${formatClock(kickoffSeconds)}` };
    case "live":
      return { title: `${home.name} ${state.board.score[0]}, ${away.name} ${state.board.score[1]}`, detail: [periodText(state.sport, state.board, state.phase), clockText(state.sport, state.board, state.phase)].filter(Boolean).join(" · ") };
    case "final": {
      const r = state.last;
      const s = r ? r.score : state.board.score;
      return { title: `Final: ${home.name} ${s[0]}, ${away.name} ${s[1]}${r?.suffix ? ` (${r.suffix})` : ""}`, detail: `Next game in ${formatClock(kickoffSeconds)}` };
    }
  }
}

/** The winner's line for a result ("Townsy FC win!", "A draw"). */
export function resultText(state: Pick<ArenaState, "home" | "away">, outcome: ArenaPick): string {
  if (outcome === "draw") return "It's a draw";
  return `${outcome === "home" ? state.home.name : state.away.name} win`;
}

/** What a pick means in words. */
export function pickLabel(state: Pick<ArenaState, "home" | "away">, pick: ArenaPick): string {
  return pick === "draw" ? "Draw" : `${pick === "home" ? state.home.name : state.away.name} win`;
}

export function picksFor(sport: ArenaSport): readonly ArenaPick[] {
  return arenaPicks(sport);
}

export const STAKES = ARENA_STAKES;

/** Chips a prediction returns if right (2x a winner, 3x a draw). */
export function potentialPayout(pick: ArenaPick, stake: number): number {
  return arenaPayout(pick, stake, pick);
}

/** Why "Predict" is unavailable right now ("" when a prediction can be made). */
export function predictionBlocker(args: {
  state: ArenaState | null;
  mine: ArenaPrivate | null;
  chips: number | null;
  chanceGamesEnabled: boolean;
  /** A guest (ADR-0048: predictions need an account; the server refuses a guest's with ACCOUNT_REQUIRED). */
  guest?: boolean;
  pick: ArenaPick | null;
  stake: number;
}): string {
  const { state, mine, chips, chanceGamesEnabled, pick, stake } = args;
  if (!chanceGamesEnabled) return "Predictions are switched off here: watch the game and cheer.";
  if (args.guest) return ACCOUNT_REQUIRED_MESSAGE;
  if (!state || !mine) return "Connecting to the stadium...";
  if (mine.prediction && mine.prediction.gameNo === state.gameNo) return "Your prediction is in for this game.";
  if (state.phase !== "pregame" || !state.predictionsOpen) return state.phase === "live" ? "Predictions closed at kickoff. They open again before the next game." : "Predictions open when the next game's countdown starts.";
  if (chips === null) return "Getting your play chips...";
  if (chips < (STAKES[0] ?? 10)) return "You need at least 10 play chips. Walk out and back in to get 100.";
  if (pick === null) return "Pick a result, then a stake.";
  if (!picksFor(state.sport).includes(pick)) return "A draw can only be picked for soccer.";
  if (stake > chips) return `Pick a smaller stake: you have ${chips} play chips.`;
  return "";
}

/** Big moment banners. */
export function momentLabel(kind: ArenaMomentKind): string {
  switch (kind) {
    case "touchdown":
      return "TOUCHDOWN!";
    case "field_goal":
      return "FIELD GOAL!";
    case "goal":
      return "GOAL!";
    case "home_run":
      return "HOME RUN!";
    case "grand_slam":
      return "GRAND SLAM!";
    case "three_pointer":
      return "THREE!";
    case "dunk":
      return "SLAM DUNK!";
    case "shootout_goal":
      return "SHOOTOUT GOAL!";
    case "final":
      return "FINAL";
  }
}

/** The crowd meter in words. */
export function crowdWord(level: number): string {
  if (level >= 75) return "Roaring";
  if (level >= 45) return "Loud";
  if (level >= 22) return "Buzzing";
  return "Quiet";
}

export function sectionLabel(state: Pick<ArenaState, "home" | "away">, section: ArenaSide): string {
  return section === "home" ? `${state.home.name} fans` : `${state.away.name} fans`;
}

/** The seat index a fan sits in (from the shared seat list), or null. */
export function seatOf(state: Pick<ArenaState, "seats"> | null, actorId: string | null): number | null {
  if (!state || !actorId) return null;
  const hit = state.seats.find(([, id]) => id === actorId);
  return hit ? hit[0] : null;
}

/** A readable foreground for text on a team colour. */
export function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return "#FFFFFF";
  const v = parseInt(m[1]!, 16);
  const r = (v >> 16) & 255;
  const g = (v >> 8) & 255;
  const b = v & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#14171F" : "#FFFFFF";
}

/** Sport names for headings. */
export const SPORT_NAMES: Readonly<Record<ArenaSport, string>> = {
  football: "American football",
  soccer: "Soccer",
  baseball: "Baseball",
  basketball: "Basketball",
  hockey: "Ice hockey",
};
